import React, { useEffect, useState , useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { getUser, clearAuth } from '../../utils/auth';
import { toast } from 'sonner';
import {
  Users, DollarSign, Bed, TrendingUp, FileText, Calendar,
  CheckCircle, UserPlus, BarChart3, PieChart, Clock, Star,
  Building2, Stethoscope, Activity, FileCheck, Send, Menu, Bell,
  LogOut, Home, UserCheck, Settings, List, Eye, Mail, Phone, MapPin, Award
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export default function HospitalDashboard() {
  const navigate = useNavigate();
  const [user] = useState(getUser);
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [doctorUtilization, setDoctorUtilization] = useState([]);
  const [revenueData, setRevenueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [classifiedInquiries, setClassifiedInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);


  const loadDashboardData = useCallback(async () => {
    try {
      const [statsRes, inquiriesRes, appointmentsRes, utilizationRes, revenueRes] = await Promise.all([
        api.get('/hospital/dashboard-stats'),
        api.get('/hospital/inquiries'),
        api.get('/hospital/appointments'),
        api.get('/hospital/doctor-utilization'),
        api.get('/hospital/revenue-data')
      ]);


      setStats(statsRes.data.stats);
      setInquiries(inquiriesRes.data.inquiries);
      setAppointments(appointmentsRes.data.appointments);
      setDoctorUtilization(utilizationRes.data.utilization);
      setRevenueData(revenueRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const loadClassifiedInquiries = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/classified-inquiries`, {
        headers: { }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClassifiedInquiries(data.inquiries || []);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    if (activeSection === 'classifieds') {
      loadClassifiedInquiries();
    }
  }, [activeSection]);

  const handleApproveInquiry = async (inquiryId) => {
    try {
      await api.put(`/hospital/inquiries/${inquiryId}/approve`);
      toast.success('Inquiry approved successfully');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to approve inquiry');
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--muted))]" data-portal="hospital">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--primary))] mx-auto"></div>
          <p className="mt-4 text-[hsl(var(--muted-foreground))]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const menuItems = [
    { icon: Home, label: 'Dashboard', section: 'dashboard' },
    { icon: List, label: 'Published Classifieds', section: 'classifieds', badge: classifiedInquiries.length },
    { icon: Calendar, label: 'Appointments', section: 'appointments' },
    { icon: UserCheck, label: 'Doctors', section: 'doctors' },
    { icon: Users, label: 'Patients', section: 'patients' },
    { icon: BarChart3, label: 'Analytics', section: 'analytics' },
    { icon: DollarSign, label: 'Revenue', section: 'revenue' },
    { icon: Settings, label: 'Settings', section: 'settings' }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-portal="hospital">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-40 h-16" data-testid="hospital-header">
        <div className="flex items-center justify-between h-full px-4">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="sidebar-toggle"
            >
              <Menu className="w-5 h-5 text-gray-700" />
            </button>
            
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-gray-900" data-testid="hospital-dashboard-title">
                  MedRahi Hospital
                </h1>
                <p className="text-xs text-gray-500">Admin Portal</p>
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <button className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* User Profile */}
            <div className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
              <div className="text-right hidden md:block">
                <p className="text-sm font-semibold text-gray-900" data-testid="user-name">{user.full_name}</p>
                <p className="text-xs text-gray-500">Hospital Admin</p>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-semibold">
                {(user.full_name || 'H')[0].toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside
          className={`fixed left-0 top-16 bottom-0 bg-white border-r border-gray-200 transition-all duration-300 z-30 ${
            sidebarOpen ? 'w-64' : 'w-0'
          } overflow-hidden`}
          data-testid="hospital-sidebar"
        >
          <nav className="p-4 space-y-2">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeSection === item.section;
              
              return (
                <button
                  key={index}
                  onClick={() => setActiveSection(item.section)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-green-50 text-green-600 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid={`menu-${item.section}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </div>
                  {item.badge > 0 && (
                    <Badge className="bg-green-600 text-white">{item.badge}</Badge>
                  )}
                </button>
              );
            })}

            {/* Logout Button */}
            <div className="pt-4 mt-4 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-all"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main
          className={`flex-1 transition-all duration-300 ${
            sidebarOpen ? 'ml-64' : 'ml-0'
          }`}
        >
          <div className="p-6">
            {/* Dashboard Section */}
            {activeSection === 'dashboard' && (
              <div>
                {/* Welcome Section */}
                <div className="mb-6">
                  <h2 className="text-2xl font-heading font-bold text-gray-900">
                    Welcome, {user.full_name}!
                  </h2>
                  <p className="text-gray-600 mt-1">
                    Oversee operations, manage doctors, and track hospital performance
                  </p>
                </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="kpi-cards">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Pending Inquiries</p>
                <p className="text-3xl font-bold text-[hsl(var(--foreground))] mt-2" data-testid="pending-inquiries">
                  {stats?.pending_inquiries || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 text-xs text-blue-600 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>Awaiting approval</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Conversion Rate</p>
                <p className="text-3xl font-bold text-[hsl(var(--foreground))] mt-2" data-testid="conversion-rate">
                  {stats?.conversion_rate || 0}<span className="text-lg">%</span>
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4 text-xs text-green-600 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              <span>Quote to booking</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Monthly Revenue</p>
                <p className="text-3xl font-bold text-[hsl(var(--foreground))] mt-2" data-testid="monthly-revenue">
                  ${(stats?.monthly_revenue || 0).toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4 text-xs text-purple-600 flex items-center gap-1">
              <Activity className="w-3 h-3" />
              <span>Current month</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Bed Occupancy</p>
                <p className="text-3xl font-bold text-[hsl(var(--foreground))] mt-2" data-testid="bed-occupancy">
                  {stats?.bed_occupancy || 0}<span className="text-lg">%</span>
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Bed className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div className="mt-4 text-xs text-orange-600 flex items-center gap-1">
              <Activity className="w-3 h-3" />
              <span>Current capacity</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="border-b border-[hsl(var(--border))]">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]'
                    : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[hsl(var(--border))]'
                }`}
                data-testid="tab-overview"
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('inquiries')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'inquiries'
                    ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]'
                    : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[hsl(var(--border))]'
                }`}
                data-testid="tab-inquiries"
              >
                Inquiry Pipeline
              </button>
              <button
                onClick={() => setActiveTab('doctors')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'doctors'
                    ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]'
                    : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[hsl(var(--border))]'
                }`}
                data-testid="tab-doctors"
              >
                Doctor Utilization
              </button>
              <button
                onClick={() => setActiveTab('revenue')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'revenue'
                    ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]'
                    : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:border-[hsl(var(--border))]'
                }`}
                data-testid="tab-revenue"
              >
                Revenue Dashboard
              </button>
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Action Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))] hover:shadow-md transition-shadow cursor-pointer" data-testid="quick-action-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileCheck className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <h3 className="font-semibold text-[hsl(var(--foreground))] mb-1">Approve Inquiry</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Review and approve patient requests</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))] hover:shadow-md transition-shadow cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Send className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <h3 className="font-semibold text-[hsl(var(--foreground))] mb-1">Send Quote</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Create and send treatment quotes</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))] hover:shadow-md transition-shadow cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <h3 className="font-semibold text-[hsl(var(--foreground))] mb-1">Assign Doctor</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Match patients with specialists</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))] hover:shadow-md transition-shadow cursor-pointer">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
                <h3 className="font-semibold text-[hsl(var(--foreground))] mb-1">Generate Invoice</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Create billing documents</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Appointment Calendar */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))]" data-testid="appointment-calendar">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold text-[hsl(var(--foreground))]">Upcoming Appointments</h3>
                  <Calendar className="w-5 h-5 text-[hsl(var(--primary))]" />
                </div>
                {appointments.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-[hsl(var(--muted-foreground))] mx-auto mb-4" />
                    <p className="text-[hsl(var(--muted-foreground))]">No upcoming appointments</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {appointments.slice(0, 5).map((apt) => (
                      <div key={apt._id} className="border border-[hsl(var(--border))] rounded-lg p-3 hover:shadow-sm transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-[hsl(var(--foreground))]">{apt.patient?.full_name || 'N/A'}</p>
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">{apt.doctor?.full_name || 'N/A'}</p>
                            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                              {apt.appointment_date} at {apt.appointment_time}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Patient Satisfaction */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))]" data-testid="patient-satisfaction">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold text-[hsl(var(--foreground))]">Patient Satisfaction</h3>
                  <Star className="w-5 h-5 text-[hsl(var(--primary))]" />
                </div>
                <div className="text-center py-8">
                  <div className="text-5xl font-bold text-[hsl(var(--foreground))] mb-2">
                    4.8
                    <span className="text-2xl text-yellow-500 ml-2">★</span>
                  </div>
                  <p className="text-[hsl(var(--muted-foreground))] mb-4">Overall Rating</p>
                  <div className="space-y-2 max-w-xs mx-auto">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[hsl(var(--muted-foreground))] w-20">Medical Care</span>
                      <div className="flex-1 h-2 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
                        <div className="h-full bg-green-500" style={{width: '92%'}}></div>
                      </div>
                      <span className="text-sm font-medium">4.6</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[hsl(var(--muted-foreground))] w-20">Facilities</span>
                      <div className="flex-1 h-2 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
                        <div className="h-full bg-green-500" style={{width: '96%'}}></div>
                      </div>
                      <span className="text-sm font-medium">4.8</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[hsl(var(--muted-foreground))] w-20">Staff</span>
                      <div className="flex-1 h-2 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
                        <div className="h-full bg-green-500" style={{width: '100%'}}></div>
                      </div>
                      <span className="text-sm font-medium">5.0</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))]">
            <h3 className="text-lg font-heading font-semibold text-[hsl(var(--foreground))] mb-4">
              Inquiry Pipeline
            </h3>
            {inquiries.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-[hsl(var(--muted-foreground))] mx-auto mb-4" />
                <p className="text-[hsl(var(--muted-foreground))]">No pending inquiries</p>
              </div>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inquiry) => (
                  <div key={inquiry._id} className="border border-[hsl(var(--border))] rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-[hsl(var(--foreground))]">{inquiry.patient?.display_name}</h4>
                          <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                            {inquiry.urgency}
                          </span>
                        </div>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">
                          <strong>Condition:</strong> {inquiry.condition}
                        </p>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">
                          <strong>Specialization:</strong> {inquiry.preferred_specialization}
                        </p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">
                          Submitted: {new Date(inquiry.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleApproveInquiry(inquiry._id)}
                          className="px-3 py-1.5 bg-[hsl(var(--primary))] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                        >
                          Approve
                        </button>
                        <button className="px-3 py-1.5 border border-[hsl(var(--border))] rounded-lg text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors">
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Doctor Utilization Tab */}
        {activeTab === 'doctors' && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))]">
            <h3 className="text-lg font-heading font-semibold text-[hsl(var(--foreground))] mb-4">
              Doctor Utilization Chart
            </h3>
            {doctorUtilization.length === 0 ? (
              <div className="text-center py-12">
                <Stethoscope className="w-12 h-12 text-[hsl(var(--muted-foreground))] mx-auto mb-4" />
                <p className="text-[hsl(var(--muted-foreground))]">No doctor data available</p>
              </div>
            ) : (
              <div className="space-y-4">
                {doctorUtilization.map((doctor) => (
                  <div key={doctor.doctor_id} className="border border-[hsl(var(--border))] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-[hsl(var(--foreground))]">{doctor.doctor_name}</h4>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{doctor.specialization}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{doctor.utilization_percentage}%</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">Utilization</p>
                      </div>
                    </div>
                    <div className="w-full h-3 bg-[hsl(var(--muted))] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 transition-all duration-300" 
                        style={{width: `${doctor.utilization_percentage}%`}}
                      ></div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-sm text-[hsl(var(--muted-foreground))]">
                      <span>Total: {doctor.total_appointments}</span>
                      <span>Completed: {doctor.completed_appointments}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue by Month */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))]">
                <h3 className="text-lg font-heading font-semibold text-[hsl(var(--foreground))] mb-4">
                  Revenue Trend (Last 6 Months)
                </h3>
                <div className="space-y-3">
                  {revenueData?.revenue_by_month.map((item) => (
                    <div key={item.month} className="flex items-center gap-4">
                      <span className="text-sm font-medium text-[hsl(var(--muted-foreground))] w-12">{item.month}</span>
                      <div className="flex-1 h-8 bg-[hsl(var(--muted))] rounded overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-end pr-3 text-white text-sm font-medium"
                          style={{width: `${(item.revenue / 130000) * 100}%`}}
                        >
                          ${(item.revenue / 1000).toFixed(0)}K
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Revenue by Treatment */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-[hsl(var(--border))]">
                <h3 className="text-lg font-heading font-semibold text-[hsl(var(--foreground))] mb-4">
                  Revenue by Treatment Type
                </h3>
                <div className="space-y-3">
                  {revenueData?.revenue_by_treatment.map((item, index) => (
                    <div key={item.treatment} className="flex items-center gap-4">
                      <span className="text-sm font-medium text-[hsl(var(--muted-foreground))] flex-1">{item.treatment}</span>
                      <div className="w-32 h-8 bg-[hsl(var(--muted))] rounded overflow-hidden">
                        <div 
                          className={`h-full flex items-center justify-end pr-3 text-white text-sm font-medium ${
                            index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : index === 2 ? 'bg-orange-500' : 'bg-purple-500'
                          }`}
                          style={{width: `${(item.revenue / 50000) * 100}%`}}
                        >
                          ${(item.revenue / 1000).toFixed(0)}K
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

                {/* Quick Access Links */}
                <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <h3 className="text-lg font-heading font-semibold text-gray-900 mb-4">
                    Quick Access Links
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <button 
                      onClick={() => setActiveSection('classifieds')}
                      className="px-4 py-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-center flex flex-col items-center gap-2"
                    >
                      <List className="w-5 h-5 text-green-600" />
                      Published Classifieds
                    </button>
                    <button 
                      onClick={() => setActiveSection('doctors')}
                      className="px-4 py-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-center flex flex-col items-center gap-2"
                    >
                      <UserCheck className="w-5 h-5 text-blue-600" />
                      Doctor Directory
                    </button>
                    <button 
                      onClick={() => setActiveSection('revenue')}
                      className="px-4 py-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-center flex flex-col items-center gap-2"
                    >
                      <DollarSign className="w-5 h-5 text-yellow-600" />
                      Package Management
                    </button>
                    <button 
                      onClick={() => setActiveSection('analytics')}
                      className="px-4 py-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors text-center flex flex-col items-center gap-2"
                    >
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                      Analytics
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Published Classifieds Section */}
            {activeSection === 'classifieds' && (
              <div>
                <div className="mb-6">
                  <h2 className="text-2xl font-heading font-bold text-gray-900">Published Classifieds</h2>
                  <p className="text-gray-600 mt-1">View patient treatment requests published for hospitals</p>
                </div>

                <Card>
                  <CardContent className="p-6">
                    {classifiedInquiries.length === 0 ? (
                      <div className="text-center py-12">
                        <List className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Published Inquiries</h3>
                        <p className="text-gray-600">Patient inquiries published by admins will appear here</p>
                      </div>
                    ) : (
                      <div className="grid gap-4">
                        {classifiedInquiries.map((inquiry) => (
                          <div 
                            key={inquiry._id} 
                            className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="text-lg font-semibold text-gray-900">{inquiry.patient_name}</h3>
                                  <Badge className={
                                    inquiry.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                                    inquiry.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                    'bg-blue-100 text-blue-800'
                                  }>
                                    {inquiry.priority || 'Normal'}
                                  </Badge>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                                  <div className="flex items-center gap-2">
                                    <Mail className="w-4 h-4" />
                                    <span>{inquiry.patient_email}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    <span>{inquiry.patient_country}</span>
                                  </div>
                                </div>

                                <div className="mb-3">
                                  <p className="text-sm font-medium text-gray-700 mb-1">Treatment Required:</p>
                                  <p className="text-base font-semibold text-green-700">{inquiry.treatment_type}</p>
                                </div>

                                <div className="mb-3">
                                  <p className="text-sm font-medium text-gray-700 mb-1">Condition Description:</p>
                                  <p className="text-sm text-gray-600">{inquiry.condition_description}</p>
                                </div>

                                {inquiry.medical_history && (
                                  <div className="mb-3">
                                    <p className="text-sm font-medium text-gray-700 mb-1">Medical History:</p>
                                    <p className="text-sm text-gray-600">{inquiry.medical_history}</p>
                                  </div>
                                )}

                                {inquiry.budget_range && (
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <DollarSign className="w-4 h-4" />
                                    <span>Budget: {inquiry.budget_range}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-3 pt-3 border-t border-gray-200">
                              <Button
                                onClick={() => {
                                  setSelectedInquiry(inquiry);
                                  setShowInquiryDialog(true);
                                }}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid="view-inquiry-details"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Full Details
                              </Button>
                              <Button
                                onClick={() => {
                                  toast.success('Response feature coming soon!');
                                }}
                                variant="outline"
                                className="border-green-600 text-green-600 hover:bg-green-50"
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Send Quote
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Other Sections Placeholders */}
            {activeSection === 'appointments' && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">Appointments</h2>
                <Card>
                  <CardContent className="p-6">
                    <div className="text-center py-12">
                      <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Appointments Management</h3>
                      <p className="text-gray-600">Appointment scheduling and management coming soon</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === 'doctors' && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">Doctors Management</h2>
                <Card>
                  <CardContent className="p-6">
                    <div className="text-center py-12">
                      <Stethoscope className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Doctor Directory</h3>
                      <p className="text-gray-600">Manage your hospital doctors and specialists</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === 'patients' && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">Patients</h2>
                <Card>
                  <CardContent className="p-6">
                    <div className="text-center py-12">
                      <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Patient Management</h3>
                      <p className="text-gray-600">View and manage patient records</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === 'analytics' && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">Analytics</h2>
                <Card>
                  <CardContent className="p-6">
                    <div className="text-center py-12">
                      <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Hospital Analytics</h3>
                      <p className="text-gray-600">Performance metrics and insights</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === 'revenue' && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">Revenue Management</h2>
                <Card>
                  <CardContent className="p-6">
                    <div className="text-center py-12">
                      <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Financial Overview</h3>
                      <p className="text-gray-600">Track revenue and billing</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeSection === 'settings' && (
              <div>
                <h2 className="text-2xl font-heading font-bold text-gray-900 mb-6">Settings</h2>
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Hospital Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hospital Name</label>
                        <input
                          type="text"
                          value={user.full_name || ''}
                          readOnly
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={user.email || ''}
                          readOnly
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                        <input
                          type="text"
                          value="Hospital Admin"
                          readOnly
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Inquiry Details Dialog */}
      <Dialog open={showInquiryDialog} onOpenChange={setShowInquiryDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Patient Inquiry Details</DialogTitle>
          </DialogHeader>
          {selectedInquiry && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Patient Name</p>
                  <p className="text-base text-gray-900">{selectedInquiry.patient_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Email</p>
                  <p className="text-base text-gray-900">{selectedInquiry.patient_email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Country</p>
                  <p className="text-base text-gray-900">{selectedInquiry.patient_country}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Priority</p>
                  <Badge className={
                    selectedInquiry.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                    selectedInquiry.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                    'bg-blue-100 text-blue-800'
                  }>
                    {selectedInquiry.priority || 'Normal'}
                  </Badge>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Treatment Type</p>
                <p className="text-lg font-semibold text-green-700">{selectedInquiry.treatment_type}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Condition Description</p>
                <p className="text-base text-gray-900">{selectedInquiry.condition_description}</p>
              </div>

              {selectedInquiry.medical_history && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Medical History</p>
                  <p className="text-base text-gray-900">{selectedInquiry.medical_history}</p>
                </div>
              )}

              {selectedInquiry.budget_range && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Budget Range</p>
                  <p className="text-base text-gray-900">{selectedInquiry.budget_range}</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowInquiryDialog(false)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    toast.success('Quote sending feature coming soon!');
                    setShowInquiryDialog(false);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Treatment Quote
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
